import React from "react";
import { useTable } from "react-table";
import tw from "tailwind-styled-components";
import { getFoodCategoryLabel, getFoodUnitLabel } from "../../../helpers";
import { capitalizeFirstLetter, truncateText } from "../../../helpers";
import { MdArrowForward } from "react-icons/md";
import { useNavigate } from "react-router-dom";

const StockTable = ({ stockTableData = [] }) => {
    const navigate = useNavigate();
    const data = React.useMemo(() => {
        if (!Array.isArray(stockTableData) || stockTableData.length === 0) {
            return [];
        }

        return stockTableData.map(item => ({
            ...item,
            categoryLabel: getFoodCategoryLabel(item.categoryId),
            unitLabel: getFoodUnitLabel(item.unitType),
            status: item.currentCount === 0 ? 'Out of Stock' :
                item.currentCount < 5 ? 'Low Stock' :
                    'In Stock',
            statusColor: item.currentCount === 0 ? 'red' :
                item.currentCount < 5 ? 'yellow' :
                    'green'
        }));
    }, [stockTableData]);

    const columns = React.useMemo(
        () => [
            {
                Header: "Item Name",
                accessor: "itemName",
                Cell: ({ row }) => (
                    <div className="flex flex-col">
                        <div className="text-sm font-medium text-gray-900 truncate max-w-[180px]">
                            {capitalizeFirstLetter(truncateText(row.original.itemName || "N/A", 28))}
                        </div>
                        <div className="text-xs text-gray-400 truncate max-w-[180px]">
                            {row.original.categoryLabel || "N/A"}
                        </div>
                    </div>
                ),
            },
            {
                Header: "Qty",
                accessor: "currentCount",
                Cell: ({ row }) => {
                    const count = row.original.currentCount || 0;
                    const unit = row.original.unitLabel || '';
                    return (
                        <div className="flex items-baseline gap-1.5">
                            <span className="text-sm font-medium text-gray-600 tabular-nums">
                                {count}
                            </span>
                            <span className="text-xs text-gray-400">
                                {unit}
                            </span>
                        </div>
                    );
                },
            },
            {
                Header: "Status",
                accessor: "status",
                Cell: ({ row }) => {
                    const status = row.original.status;
                    const color = row.original.statusColor;

                    const statusConfig = {
                        'Out of Stock': {
                            bg: 'bg-red-50',
                            text: 'text-red-600',
                            dot: 'bg-red-500'
                        },
                        'Low Stock': {
                            bg: 'bg-yellow-50',
                            text: 'text-yellow-600',
                            dot: 'bg-yellow-500'
                        },
                        'In Stock': {
                            bg: 'bg-green-50',
                            text: 'text-green-600',
                            dot: 'bg-green-500'
                        }
                    };

                    const config = statusConfig[status] || statusConfig['In Stock'];

                    return (
                        <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
                            {/* <span className={`w-2 h-2 rounded-full ${config.dot}`} /> */}
                            {status}
                        </div>
                    );
                },
            },
        ],
        []
    );

    const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
        useTable({
            columns,
            data,
        });

    // Calculate summary stats
    const summaryStats = React.useMemo(() => {
        if (!data || data.length === 0) {
            return {
                totalItems: 0,
                inStock: 0,
                lowStock: 0,
                outOfStock: 0
            };
        }

        const inStock = data.filter(item => item.currentCount >= 5).length;
        const lowStock = data.filter(item => item.currentCount > 0 && item.currentCount < 5).length;
        const outOfStock = data.filter(item => item.currentCount === 0).length;

        return {
            totalItems: data.length,
            inStock,
            lowStock,
            outOfStock
        };
    }, [data]);

    return (
        <Container>
            {/* Header */}
            <Header>
                <Title>Stock Inventory</Title>
                <ViewAll onClick={() => navigate("/inventory-stock")}>
                    View all <MdArrowForward className="ml-1" />
                </ViewAll>
            </Header>

            {/* Table */}
            <TableWrapper>
                <Table {...getTableProps()}>
                    <Thead>
                        {headerGroups.map((headerGroup) => (
                            <tr {...headerGroup.getHeaderGroupProps()} key={headerGroup.id}>
                                {headerGroup.headers.map((column) => (
                                    <Th {...column.getHeaderProps()} className={column.headerClassName} key={column.id}>
                                        {column.render("Header")}
                                    </Th>
                                ))}
                            </tr>
                        ))}
                    </Thead>

                    <Tbody {...getTableBodyProps()}>
                        {rows.length > 0 ? (
                            rows.map((row) => {
                                prepareRow(row);
                                const status = row.original.status;
                                const isOutOfStock = status === 'Out of Stock';
                                const isLowStock = status === 'Low Stock';

                                return (
                                    <Tr
                                        {...row.getRowProps()}
                                        key={row.id}
                                        $outOfStock={isOutOfStock}
                                        $lowStock={isLowStock}
                                    >
                                        {row.cells.map((cell) => (
                                            <Td {...cell.getCellProps()} className={cell.column.className} key={cell.column.id}>
                                                {cell.render("Cell")}
                                            </Td>
                                        ))}
                                    </Tr>
                                );
                            })
                        ) : (
                            <tr>
                                <Td colSpan={3} className="text-center py-8 text-gray-400">
                                    No stock items found
                                </Td>
                            </tr>
                        )}
                    </Tbody>
                </Table>
            </TableWrapper>
        </Container>
    );
};

const Container = tw.div`
    bg-white rounded-lg p-5 
`;

const Header = tw.div`
    flex items-center justify-between mb-4
`;

const Title = tw.h3`
    text-sm font-semibold text-gray-700 uppercase tracking-wide
`;

const Subtitle = tw.p`
    text-xs text-gray-400 mt-0.5
`;


const TableWrapper = tw.div`
    overflow-x-auto
`;

const Table = tw.table`
    min-w-full border-separate border-spacing-y-1
`;

const Thead = tw.thead``;

const Th = tw.th`
    bg-gray-100 px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider
   
`;
// first:rounded-l-lg last:rounded-r-lg
const Tbody = tw.tbody``;

const Tr = tw.tr`
    border-b border-gray-100
    hover:bg-sky-50
    transition-colors
    ${props => props.$outOfStock ? ' hover:bg-red-50' : ''}
    ${props => props.$lowStock ? ' hover:bg-yellow-50' : ''}
`;

const Td = tw.td`
    px-4 py-3 text-sm text-gray-700
   
`;
const ViewAll = tw.button`
  text-sm text-blue-600 hover:text-blue-700
  flex items-center font-medium
`;
// first:rounded-l-lg last:rounded-r-lg
export default StockTable;